import { useEffect, useRef, useState } from "react";
import { Icon } from "../icons";
import type { MediaDownload, Message } from "../types";

type PreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; media: MediaDownload }
  | { status: "error" };

export function MessageMediaPreview({
  message,
  onResolve,
}: {
  message: Message;
  onResolve: () => Promise<MediaDownload>;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const resolveRef = useRef(onResolve);
  const [preview, setPreview] = useState<PreviewState>({ status: "idle" });
  const canPreview = isInlinePreviewType(message.type) && message.mediaStatus === "stored";
  resolveRef.current = onResolve;

  useEffect(() => {
    setPreview({ status: "idle" });
    if (!canPreview) return;

    let active = true;
    let requested = false;
    const resolve = () => {
      if (requested) return;
      requested = true;
      setPreview({ status: "loading" });
      void resolveRef.current()
        .then((media) => {
          if (active) setPreview({ status: "ready", media });
        })
        .catch(() => {
          if (active) setPreview({ status: "error" });
        });
    };

    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      resolve();
      return () => {
        active = false;
      };
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        resolve();
        observer.disconnect();
      }
    }, { rootMargin: "160px" });
    observer.observe(node);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [canPreview, message.id]);

  if (message.mediaStatus !== "stored") {
    return (
      <div className="media-placeholder unavailable" ref={rootRef}>
        <Icon name={mediaIcon(message.type)} size={18} />
        <span>{mediaTitle(message.type)}</span>
        <small>{unavailableReason(message)}</small>
      </div>
    );
  }

  if (message.type === "document" || !isInlinePreviewType(message.type)) {
    return (
      <div className="media-placeholder stored" ref={rootRef}>
        <Icon name="document" size={18} />
        <span>{message.mediaFileName || mediaTitle(message.type)}</span>
        {message.mediaSizeBytes ? <small>{formatBytes(message.mediaSizeBytes)}</small> : null}
      </div>
    );
  }

  return (
    <div className={`media-preview ${message.type}`} ref={rootRef}>
      {preview.status === "idle" || preview.status === "loading" ? (
        <div className="media-preview-loading"><span /> Cargando vista previa…</div>
      ) : preview.status === "error" ? (
        <div className="media-placeholder unavailable">
          <Icon name={mediaIcon(message.type)} size={18} />
          <span>Vista previa no disponible</span>
          <small>Usa “Abrir {mediaLabel(message.type)}” para intentarlo nuevamente.</small>
        </div>
      ) : (
        <ResolvedMediaPreview media={preview.media} message={message} />
      )}
    </div>
  );
}

export function ResolvedMediaPreview({ message, media }: { message: Message; media: MediaDownload }) {
  if (message.type === "image") {
    return (
      <a aria-label="Abrir imagen en tamaño completo" className="media-preview-link" href={media.downloadUrl} rel="noreferrer" target="_blank">
        <img alt={message.body ? `Imagen: ${message.body}` : "Imagen adjunta"} loading="lazy" src={media.downloadUrl} />
      </a>
    );
  }
  if (message.type === "video") {
    return (
      <video controls playsInline preload="metadata" src={media.downloadUrl}>
        Tu navegador no puede reproducir este video.
      </video>
    );
  }
  return (
    <audio controls preload="metadata" src={media.downloadUrl}>
      Tu navegador no puede reproducir este audio.
    </audio>
  );
}

export function isInlinePreviewType(type: Message["type"]) {
  return type === "image" || type === "video" || type === "audio";
}

export function unavailableReason(message: Message) {
  if (message.mediaStatus === "failed") return "El archivo no pudo almacenarse.";
  if (message.mediaStatus === "skipped") return "El archivo fue omitido por la política de captura.";
  return "WhatsApp no entregó el archivo original; solo quedó su referencia.";
}

function mediaTitle(type: Message["type"]) {
  if (type === "audio") return "Nota de voz";
  if (type === "image") return "Imagen no disponible";
  if (type === "video") return "Video no disponible";
  return "Documento no disponible";
}

function mediaLabel(type: Message["type"]) {
  if (type === "audio") return "audio";
  if (type === "image") return "imagen";
  if (type === "video") return "video";
  return "archivo";
}

function mediaIcon(type: Message["type"]) {
  if (type === "document") return "document" as const;
  if (type === "audio") return "clock" as const;
  return "paperclip" as const;
}

function formatBytes(value: number) {
  if (value < 1_024) return `${value} B`;
  if (value < 1_048_576) return `${(value / 1_024).toFixed(1)} KB`;
  return `${(value / 1_048_576).toFixed(1)} MB`;
}
